import type {TypedDocumentNode} from '@apollo/client';
import {gql, NetworkStatus} from '@apollo/client';
import {useQuery} from '@apollo/client/react';

import useProgressVariables from '../../hooks/useProgressVariables';
import type {_Object} from '../../Paramut';
import staticFallbackQuote from '../../StaticMaterials/fallbackQuote';
import staticFallbackTopic from '../../StaticMaterials/fallbackTopic';
import type {Millisecond} from '../../Time';
import first from '../../util/first';
import type {
  GetDailyContentQuery,
  GetDailyContentQueryVariables,
} from './useDailyContent.generated';

export type UseDailyContentOptions = {
  period?: Millisecond;
  skip?: boolean;
};

export type DailyContent<Mut extends boolean = false> = _Object<
  {
    quoteId: number | null | undefined;
    quoteBody: string | null | undefined;
    quoteSource: string | null | undefined;
    topicId: number | null | undefined;
    topicBody: string | null | undefined;
  },
  Mut
>;

export default function useDailyContent(
  now: Millisecond,
  options?: UseDailyContentOptions,
): DailyContent {
  const {period, skip} = options ?? {};
  const {daysReached, day, month} = useProgressVariables(now);

  const result = useQuery(GET_DAILY_CONTENT, {
    skip,
    variables: {daysReached: daysReached ?? 0, day, month},
    returnPartialData: true,
    fetchPolicy: 'cache-and-network',
    pollInterval: __DEV__ ? period : 0,
  });

  const quoteByDaysReached =
    daysReached === undefined
      ? undefined
      : first(result.data?.QuotesByDaysReached?.docs);
  const quoteByAnnualDate = first(result.data?.QuotesByAnnualDate?.docs);
  const fallbackQuote = result.data?.FallbackPublication?.quote;

  const topicByDaysReached =
    daysReached === undefined
      ? undefined
      : first(result.data?.TopicsByDaysReached?.docs);
  const topicByAnnualDate = first(result.data?.TopicsByAnnualDate?.docs);
  const fallbackTopic = result.data?.FallbackPublication?.topic;

  const remoteQuote = quoteByDaysReached ?? quoteByAnnualDate ?? fallbackQuote;
  const remoteTopic = topicByDaysReached ?? topicByAnnualDate ?? fallbackTopic;

  const quote =
    !remoteQuote &&
    result.networkStatus !== NetworkStatus.loading &&
    result.networkStatus !== NetworkStatus.setVariables
      ? {id: null, ...staticFallbackQuote}
      : remoteQuote;

  const topic =
    !remoteTopic &&
    result.networkStatus !== NetworkStatus.loading &&
    result.networkStatus !== NetworkStatus.setVariables
      ? {id: null, ...staticFallbackTopic}
      : remoteTopic;

  return {
    quoteId: quote?.id,
    quoteBody: quote?.body,
    topicId: topic?.id,
    quoteSource: quote?.source,
    topicBody: topic?.body,
  };
}

const GET_DAILY_CONTENT: TypedDocumentNode<
  GetDailyContentQuery,
  GetDailyContentQueryVariables
> = gql`
  query GetDailyContent($daysReached: Float!, $day: Float!, $month: Float!) {
    QuotesByDaysReached: Quotes(
      limit: 1
      pagination: false
      where: {daysReached: {equals: $daysReached}}
    ) {
      docs {
        ...QuoteContent
      }
    }

    TopicsByDaysReached: Topics(
      limit: 1
      pagination: false
      where: {daysReached: {equals: $daysReached}}
    ) {
      docs {
        ...TopicContent
      }
    }

    QuotesByAnnualDate: Quotes(
      limit: 1
      pagination: false
      where: {
        annualDate__day: {equals: $day}
        annualDate__month: {equals: $month}
      }
    ) {
      docs {
        ...QuoteContent
      }
    }

    TopicsByAnnualDate: Topics(
      limit: 1
      pagination: false
      where: {
        annualDate__day: {equals: $day}
        annualDate__month: {equals: $month}
      }
    ) {
      docs {
        ...TopicContent
      }
    }

    FallbackPublication {
      quote {
        ...QuoteContent
      }
      topic {
        ...TopicContent
      }
    }
  }

  fragment QuoteContent on Quote {
    id
    body
    source
  }

  fragment TopicContent on Topic {
    id
    body
  }
`;
